import { useCallback, useEffect, useRef, useState } from 'react';
import uuidv4 from 'uuid/v4';
import { createUploadFileForm } from '../../utils/imageHelper';
import {
  getPhotoUploadError,
  getPhotoValidationError,
  getResponseStatus,
  getUploadedImageUrl,
  MAX_REVIEW_PHOTOS,
} from './CustomerReviewCreate.helpers';

const makePhoto = file => {
  const validationError = getPhotoValidationError(file);
  return {
    error: validationError,
    file,
    id: uuidv4(),
    imageUrl: null,
    previewUrl: URL.createObjectURL(file),
    status: validationError ? 'error' : 'uploading',
  };
};

const useCustomerReviewPhotos = ({ invoiceNumber, uploadImage }) => {
  const [activeUploadCount, setActiveUploadCount] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [photoAlert, setPhotoAlert] = useState(null);
  const activeUploadsRef = useRef(new Set());
  const generationRef = useRef(0);
  const invoiceRef = useRef(invoiceNumber);
  const isMountedRef = useRef(true);
  const photosRef = useRef([]);
  const uploadRequestIdRef = useRef(0);

  invoiceRef.current = invoiceNumber;

  useEffect(() => () => {
    isMountedRef.current = false;
    generationRef.current += 1;
    activeUploadsRef.current.clear();
    photosRef.current.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
  }, []);

  const replacePhotos = useCallback(nextPhotos => {
    photosRef.current = nextPhotos;
    setPhotos(nextPhotos);
  }, []);

  const updatePhoto = useCallback((photoId, update) => {
    const nextPhotos = photosRef.current.map(photo => (
      photo.id === photoId ? { ...photo, ...update } : photo
    ));
    replacePhotos(nextPhotos);
  }, [replacePhotos]);

  const uploadPhoto = useCallback(async (photo, generation, targetInvoice) => {
    const uploadRequestId = uploadRequestIdRef.current + 1;
    uploadRequestIdRef.current = uploadRequestId;
    activeUploadsRef.current.add(uploadRequestId);
    setActiveUploadCount(activeUploadsRef.current.size);

    try {
      const payload = createUploadFileForm(photo.file, false, {
        invoice_number: targetInvoice,
      });
      const response = await uploadImage(payload);
      if (
        !isMountedRef.current
        || generationRef.current !== generation
        || invoiceRef.current !== targetInvoice
      ) return;

      const imageUrl = getUploadedImageUrl(response);
      updatePhoto(photo.id, imageUrl
        ? { error: '', imageUrl, status: 'success' }
        : { error: getPhotoUploadError(null), status: 'error' });
    } catch (error) {
      if (
        !isMountedRef.current
        || generationRef.current !== generation
        || invoiceRef.current !== targetInvoice
      ) return;
      if (getResponseStatus(error) === 401) return;

      updatePhoto(photo.id, {
        error: getPhotoUploadError(error),
        imageUrl: null,
        status: 'error',
      });
    } finally {
      activeUploadsRef.current.delete(uploadRequestId);
      if (isMountedRef.current && generationRef.current === generation) {
        setActiveUploadCount(activeUploadsRef.current.size);
      }
    }
  }, [updatePhoto, uploadImage]);

  const addPhotos = useCallback(async files => {
    const selectedFiles = Array.from(files || []);
    if (!invoiceRef.current || selectedFiles.length === 0) return;

    const remainingCapacity = MAX_REVIEW_PHOTOS - photosRef.current.length;
    const filesToAdd = selectedFiles.slice(0, remainingCapacity);
    const rejectedCount = selectedFiles.length - filesToAdd.length;
    setPhotoAlert(rejectedCount > 0 ? {
      color: 'danger',
      messages: [
        `${rejectedCount} photo(s) could not be added. A review carries at most 3.`,
      ],
    } : null);

    const newPhotos = filesToAdd.map(makePhoto);
    replacePhotos([...photosRef.current, ...newPhotos]);

    const generation = generationRef.current;
    const targetInvoice = invoiceRef.current;
    await Promise.allSettled(newPhotos
      .filter(photo => photo.status === 'uploading')
      .map(photo => uploadPhoto(photo, generation, targetInvoice)));
  }, [replacePhotos, uploadPhoto]);

  const removePhoto = useCallback(photoId => {
    const photo = photosRef.current.find(item => item.id === photoId);
    if (photo) URL.revokeObjectURL(photo.previewUrl);
    replacePhotos(photosRef.current.filter(item => item.id !== photoId));
    setPhotoAlert(null);
  }, [replacePhotos]);

  const retryPhoto = useCallback(async photoId => {
    const photo = photosRef.current.find(item => item.id === photoId);
    if (!photo || !invoiceRef.current) return;

    const validationError = getPhotoValidationError(photo.file);
    if (validationError) {
      updatePhoto(photoId, { error: validationError, status: 'error' });
      setPhotoAlert(null);
      return;
    }

    const retryingPhoto = {
      ...photo,
      error: '',
      imageUrl: null,
      status: 'uploading',
    };
    updatePhoto(photoId, retryingPhoto);
    setPhotoAlert(null);
    await uploadPhoto(retryingPhoto, generationRef.current, invoiceRef.current);
  }, [updatePhoto, uploadPhoto]);

  const clearPhotos = useCallback((message = '') => {
    if (
      photosRef.current.length === 0
      && activeUploadsRef.current.size === 0
      && !message
    ) {
      setPhotoAlert(null);
      return;
    }

    generationRef.current += 1;
    activeUploadsRef.current.clear();
    setActiveUploadCount(0);
    photosRef.current.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
    replacePhotos([]);
    setPhotoAlert(message ? { color: 'warning', messages: [message] } : null);
  }, [replacePhotos]);

  const setValidationMessages = useCallback(messages => {
    setPhotoAlert(messages.length > 0 ? { color: 'danger', messages } : null);
  }, []);

  const clearPhotoAlert = useCallback(() => setPhotoAlert(null), []);

  const isUploading = activeUploadCount > 0;
  const imageUrls = photos
    .filter(photo => photo.status === 'success' && photo.imageUrl)
    .map(photo => photo.imageUrl);

  return {
    addPhotos,
    clearPhotoAlert,
    clearPhotos,
    imageUrls,
    isUploading,
    photoAlert,
    photos,
    removePhoto,
    retryPhoto,
    setValidationMessages,
  };
};

export default useCustomerReviewPhotos;
